import { useCallback, useEffect, useState } from "react";
import { User, UserSchema } from "../models/user";
import { fetchFastApi } from "../api/fastapi";
import { jwtDecode } from "jwt-decode";
import {
  Workspace,
  WorkspaceSchema,
  WorkspaceMember,
  WorkspaceMemberSchema,
  WorkspaceSummary,
  WorkspaceSummarySchema,
} from "../models/workspace";
import {
  AnalyticsDestination,
  AnalyticsDestinationSchema,
  AnalyticsIdentity,
  parseAnalyticsIdentity,
} from "../models/analytics";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { stashPostLoginRedirect } from "../lib/authRedirect";

export const clearAuthAndRedirect = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("workspace_id");
  if (window.location.pathname !== "/signin") {
    stashPostLoginRedirect(
      window.location.pathname + window.location.search + window.location.hash,
    );
    window.location.href = "/signin";
  }
};

// The parsed shape `fetchUser` resolves with. Declared once at module scope
// so the bridge below (registered from inside the hook) can be typed without
// reaching into the hook's closure.
type FetchUserResult = {
  parsedUser?: User;
  parsedWorkspace?: Workspace;
  parsedWorkspaceMember?: WorkspaceMember;
  parsedWorkspaces: WorkspaceSummary[];
};

// Module-level bridge so fetchFastApi can trigger a silent /user/ refetch
// (for the reactive-on-403 pattern) without depending on React context.
// Every mounted `useCheckTokenAndFetchUser` hook registers itself and the
// most recent registration wins. A stack rather than a single slot because
// the hook mounts twice on the SSO path (App and SSOCallback): with one
// slot, SSOCallback's unmount cleanup emptied the bridge for the rest of
// the SPA session, so post-SSO callers (the accept-terms flow) got null
// and could never rehydrate without a full page reload.
const registeredRefetchers: Array<() => Promise<FetchUserResult>> = [];
let inFlightUserRefetch: Promise<FetchUserResult> | null = null;

// Re-fetches /user/ through the registered hook instance and returns the
// parsed result (or null if there's nothing registered yet, or the refetch
// failed). Callers that only want the reactive-on-403 side effect can ignore
// the return value; callers that need to synchronously push the fresh data
// into the auth context (e.g. PartnerTermsPage, to avoid racing on a stale
// `acceptedTerms` before navigating) can use it directly.
export const refetchUserPermissions = async (options?: {
  fresh?: boolean;
}): Promise<FetchUserResult | null> => {
  const registeredRefetchUser = registeredRefetchers.at(-1);
  if (!registeredRefetchUser) return null;
  if (inFlightUserRefetch) {
    if (!options?.fresh) {
      // Dedupe: coalesce concurrent refetches into a single request; every
      // caller observes the same resolved result (or null if it failed).
      return inFlightUserRefetch.catch(() => null);
    }
    // `fresh` callers just changed server state and cannot use a response that
    // was already in flight before that write landed — it is a snapshot from
    // before. Let the in-flight one settle, then issue our own.
    await inFlightUserRefetch.catch(() => null);
  }
  const ourRefetch = registeredRefetchUser();
  inFlightUserRefetch = ourRefetch;
  try {
    return await ourRefetch;
  } catch (error) {
    // Silent: a 403-triggered refetch's original error is already
    // surfaced via ApiError; other callers surface their own error.
    console.error("Silent /user/ refetch failed", error);
    return null;
  } finally {
    // Only clear the slot if it is still ours: a `fresh` caller that waited
    // for us has already replaced it, and clearing unconditionally would drop
    // their request from the dedupe path.
    if (inFlightUserRefetch === ourRefetch) {
      inFlightUserRefetch = null;
    }
  }
};

export const refetchAccessToken = async () => {
  const idToken = localStorage.getItem("id_token");
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken || !idToken) {
    clearAuthAndRedirect();
    return false;
  }

  try {
    const res = await fetchFastApi({
      method: "POST",
      body: { refresh_token: refreshToken, id_token: idToken },
      path: "/auth/refresh-tokens",
    });
    if (res?.access_token) {
      localStorage.setItem("access_token", res.access_token);
      localStorage.setItem("id_token", res.id_token);
      return true;
    }
    clearAuthAndRedirect();
    return false;
  } catch {
    clearAuthAndRedirect();
    return false;
  }
};

export const useCheckTokenAndFetchUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [analyticsDestination, setAnalyticsDestination] =
    useState<AnalyticsDestination | null>(null);
  const [analyticsIdentity, setAnalyticsIdentity] =
    useState<AnalyticsIdentity | null>(null);
  const [workspaceMember, setWorkspaceMember] =
    useState<WorkspaceMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const checkAndRefreshToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    const accessToken = localStorage.getItem("access_token");
    const idToken = localStorage.getItem("id_token");

    if (!refreshToken || !idToken || !accessToken) {
      console.log("missing token");
      setIsAuthenticated(false);
      return;
    }

    const decodedAccessToken = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;
    const timeLeft = decodedAccessToken.exp! - currentTime;
    if (timeLeft > 900) {
      console.log("setting access token");
      setIsAuthenticated(true);
      setAccessToken(accessToken);
      return accessToken;
    }

    try {
      console.log("Refetching access token.");
      const refetchResponse = await refetchAccessToken();
      if (refetchResponse) {
        setIsAuthenticated(true);
        return accessToken;
      } else {
        setIsAuthenticated(false);
        return;
      }
    } catch (e) {
      console.error(e);
      setIsAuthenticated(false);
      return;
    }
  };
  const fetchUser = useCallback(async (options?: { silent?: boolean }) => {
    try {
      const response = await fetchFastApi({ method: "GET", path: "/user/" });
      let parsedUser;
      let parsedWorkspace;
      let parsedWorkspaceMember;
      let parsedWorkspaces: WorkspaceSummary[] = [];
      let parsedAnalyticsDestination: AnalyticsDestination | null = null;
      let parsedAnalyticsIdentity: AnalyticsIdentity | null = null;
      if (response.user) {
        parsedUser = UserSchema.parse(response.user);
        setUser(parsedUser);
        Sentry.setUser({
          id: parsedUser.id,
          email: parsedUser.email,
        });
      } else {
        throw new Error("Failed to fetch user");
      }
      if (response.workspace) {
        parsedWorkspace = WorkspaceSchema.parse(response.workspace);
        setWorkspace(parsedWorkspace);
        // Always reflect the server's resolved workspace in localStorage so a
        // stale stored id (revoked membership, picker race) self-heals.
        if (
          response.workspaceReset === true ||
          localStorage.getItem("workspace_id") !== parsedWorkspace.id
        ) {
          localStorage.setItem("workspace_id", parsedWorkspace.id);
        }
      }
      if (response.member) {
        parsedWorkspaceMember = WorkspaceMemberSchema.parse(response.member);
        setWorkspaceMember(parsedWorkspaceMember);
      }
      // Which Amplitude project this session reports to, decided server-side
      // from the workspace it just resolved (MS-3413). Arrives with that
      // workspace so the two can never disagree.
      parsedAnalyticsDestination = AnalyticsDestinationSchema.parse(
        response.analyticsDestination,
      );
      setAnalyticsDestination(parsedAnalyticsDestination);
      parsedAnalyticsIdentity = parseAnalyticsIdentity(response);
      setAnalyticsIdentity(parsedAnalyticsIdentity);
      if (Array.isArray(response.workspaces)) {
        parsedWorkspaces = response.workspaces.map((w: unknown) =>
          WorkspaceSummarySchema.parse(w),
        );
        setWorkspaces(parsedWorkspaces);
      }
      return {
        parsedUser,
        parsedWorkspace,
        parsedWorkspaceMember,
        parsedWorkspaces,
        parsedAnalyticsDestination,
        parsedAnalyticsIdentity,
      };
    } catch (e) {
      console.error(e);
      if (!options?.silent) {
        toast.error("Error fetching user and workspace");
      }
      throw new Error("Failed to fetch user");
    }
  }, []);

  useEffect(() => {
    const refetchSilently = () => fetchUser({ silent: true });
    registeredRefetchers.push(refetchSilently);
    return () => {
      // Remove only this instance's registration; another instance's entry
      // (App's, which outlives SSOCallback's) must survive the unmount.
      const registrationIndex = registeredRefetchers.indexOf(refetchSilently);
      if (registrationIndex !== -1) {
        registeredRefetchers.splice(registrationIndex, 1);
      }
    };
  }, [fetchUser]);

  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      setLoading(false);
      return;
    }

    const fetchUserAndCheckToken = async () => {
      setLoading(true);
      try {
        await checkAndRefreshToken();
        await fetchUser();
      } catch (e) {
        console.error(e);
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndCheckToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    user,
    workspace,
    workspaces,
    workspaceMember,
    analyticsDestination,
    analyticsIdentity,
    loading,
    error,
    isAuthenticated,
    accessToken,
    checkAndRefreshToken,
    fetchUser,
  };
};
